import { Autocomplete, FormControlLabel, Grid, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { filter, find, map } from "lodash";
import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";

import DeliveryAndCollectionFields, {
  type CollectableDeliveryFieldErrors,
} from "./DeliveryAndCollectionFields";
import { getShiftOptions, handleShiftChange } from "./schedulerHelperMethods";
import { ScheduleDataType, Shift } from "./types";
import { CreateSingleScheduleMutation, Address, GetCompanyResourcesQuery, CompanyResourceResourceTypeEnum, GetCompanyResourcesDocument, GetProjectWorkingHoursDocument, CreateSingleScheduleDocument, ScheduleGuestUserDocument, GetSchedulesDocument } from "@/graphql/generated/graphql";
import { getInUTC } from "@/utils/dateTimeHelpers";
import { formatAddress, arrayLength } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";
import OverwatchModal from "../OverwatchModal";
import OverwatchResourceSelector from "../OverwatchResourceSelector";
import TimeScheduleSection from "../TimeScheduleSection";

dayjs.extend(utc);

interface NewScheduleModalProps {
  resourceType?: string | undefined;
  reportDate: string;
  projectId: string;
  reportId: string;
  projectShifts?: string;
  handleClose: () => void;
  handleSuccess?: (response: CreateSingleScheduleMutation) => void;
  addresses: Address[];
}

function optionsFormatter(optionsArray: GetCompanyResourcesQuery["getCompanyResources"], resource: string) {
  const data = map(filter(optionsArray, (opt) => opt.resourceType === resource), (option) => ({
    id: option.id,
    name: option.name,
  }));

  return data;
}

const NewScheduleModal: React.FC<NewScheduleModalProps> = ({
  resourceType,
  reportDate,
  projectId,
  reportId,
  projectShifts,
  handleClose,
  handleSuccess,
  addresses
}) => {
  const [selectedShift, setSelectedShift] = useState<string | null>(null);
  const [newResourceType, setNewResourceType] = useState<string>(CompanyResourceResourceTypeEnum.Human);
  const [collectable, setCollectable] = useState<boolean>(false);
  const [onHire, setOnHire] = useState<boolean>(false);
  const [collectableFieldErrors, setCollectableFieldErrors] =
    useState<CollectableDeliveryFieldErrors>({
      supplier: false,
      delivererName: false,
      receiverName: false,
    });

  const mainAddress = find((addresses || []), (address) => address.locationType === "main");
  const subLocations = filter((addresses || []), (address) => address.locationType !== "main");

  const [selectedResource, setSelectedResource] = useState<{ id: string, name: string, lastName?: string }>({
    id: "",
    name: "",
    lastName: "",
  });

  const [scheduleData, setScheduleData] = useState<ScheduleDataType>({
    startTime: dayjs(
      new Date(reportDate as string).setHours(8, 0)
    ),
    endTime: dayjs(
      new Date(reportDate as string).setHours(17, 0)
    ),
    addressId: mainAddress?.id as string,
    supplier: "",
    delivererName: "",
    receiverName: "",
  });

  const { data: companyResources } = useQuery(GetCompanyResourcesDocument);

  const { data: workingHours } = useQuery(GetProjectWorkingHoursDocument, {
    variables: {
      projectId
    }
  });

  const [scheduleExistingUser] = useMutation(CreateSingleScheduleDocument);
  const [scheduleGuestUser] = useMutation(ScheduleGuestUserDocument);

  const disableSubmit = () => {
    const isEquipment =
      (resourceType || newResourceType) ===
      CompanyResourceResourceTypeEnum.Equipment;
    const collectableFieldsIncomplete =
      collectable &&
      isEquipment &&
      (!scheduleData.supplier?.toString().trim() ||
        !scheduleData.delivererName?.toString().trim() ||
        !scheduleData.receiverName?.toString().trim());

    return (
      selectedResource.id === "" ||
      selectedResource.name === "" ||
      collectableFieldsIncomplete
    );
  };

  const handleSubmit = () => {
    if (selectedResource.id === "") return;

    const isEquipment =
      (resourceType || newResourceType) ===
      CompanyResourceResourceTypeEnum.Equipment;
    if (isEquipment && collectable) {
      const supplierOk = scheduleData.supplier?.toString().trim();
      const delivererOk = scheduleData.delivererName?.toString().trim();
      const receiverOk = scheduleData.receiverName?.toString().trim();
      if (!supplierOk || !delivererOk || !receiverOk) {
        setCollectableFieldErrors({
          supplier: !supplierOk,
          delivererName: !delivererOk,
          receiverName: !receiverOk,
        });
        displayNotification({
          text: "Please fill in all required fields",
          type: "error",
        });
        return;
      }
    }

    const baseInfo = {
      projectId,
      reportId,
      date: dayjs(reportDate as string).format("DD-MM-YYYY"),
      startTime: getInUTC(scheduleData.startTime),
      endTime: getInUTC(scheduleData.endTime),
      source: scheduleData?.source as string,
      title: scheduleData?.title as string,
      addressId: scheduleData?.addressId as string,
      ...((resourceType || newResourceType) === CompanyResourceResourceTypeEnum.SubContractor && { descriptionOfService: scheduleData.descriptionOfService }),
      ...((resourceType || newResourceType) === CompanyResourceResourceTypeEnum.Equipment && {
        collectable,
        onHire,
        ...(collectable
          ? {
            supplier: scheduleData.supplier?.toString().trim() ?? "",
            delivererName: scheduleData.delivererName?.toString().trim() ?? "",
            receiverName: scheduleData.receiverName?.toString().trim() ?? "",
          }
          : {}),
      })
    };

    const refetchItems = [
      "getReportForSelectedDate",
      "getUserNotifications",
      "getReport",
      {
        query: GetSchedulesDocument,
        variables: {
          date: reportDate,
          projectId,
          resourceType,
        },
      },
    ];

    if (selectedResource.id === "0") {
      scheduleGuestUser({
        variables: {
          scheduleInfo: {
            name: `${selectedResource.name} ${selectedResource.lastName}` as string,
            resourceType: resourceType ? resourceType : newResourceType,
            ...baseInfo,
          },
        },
        refetchQueries: refetchItems,
        onError: () => alert("Unable to schedule guest user"),
      });
    } else {
      scheduleExistingUser({
        variables: {
          scheduleInfo: {
            companyResourceId: selectedResource.id as string,
            ...baseInfo,
          },
        },
        refetchQueries: refetchItems,
        onCompleted: (response) => handleSuccess && handleSuccess(response),
        onError: (error) => console.log(error.message),
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getSchedulesForScheduler"
          });
          cache.gc();
        }
      });
    }
    handleClose();
  };

  const handleScheduleTimeChange = (field: 'startTime' | 'endTime', value: Dayjs | null) => {
    setScheduleData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (workingHours?.getProjectWorkingHours) {
      const { start, end } = workingHours.getProjectWorkingHours.hours;
      const startTime = dayjs(
        new Date(reportDate as string).setHours(
          dayjs(start).hour(),
          dayjs(start).minute()
        )
      );
      const endTime = dayjs(
        new Date(reportDate as string).setHours(
          dayjs(end).hour(),
          dayjs(end).minute()
        )
      );

      setScheduleData((prev) => ({ ...prev, startTime, endTime }));
    }
  }, [reportDate, workingHours?.getProjectWorkingHours]);

  const getResourceInfo = () => {
    const switchBase = resourceType ? resourceType : newResourceType;
    switch (switchBase) {
      case CompanyResourceResourceTypeEnum.Human:
        return { id: "employee-selector", type: "Employee", title: "Add Entry to People Register" };
      case CompanyResourceResourceTypeEnum.Equipment:
        return { id: "equipment-selector", type: "Equipment", title: "Add Entry to Equipment Register" };
      default:
        return { id: "sub-contractor-selector", type: "Contractor", title: "Add Entry to Sub Contractor Register" };
    }
  };

  const isExternalEquipment = () => {
    const equipment = find((companyResources?.getCompanyResources || []), (equipment) => equipment.id === selectedResource.id);

    return equipment && equipment.resourceMeta?.equipmentType === "external";
  }

  const showEquipmentOnlySections = (resourceType || newResourceType) === CompanyResourceResourceTypeEnum.Equipment;
  const showPeopleOnlySections = (resourceType || newResourceType) === CompanyResourceResourceTypeEnum.Human;
  const showContractorOnlySections = (resourceType || newResourceType) === CompanyResourceResourceTypeEnum.SubContractor;

  return (
    <OverwatchModal
      title={getResourceInfo().title}
      primaryButtonText="Add Resource"
      size="sm"
      primaryButtonAction={handleSubmit}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      disablePrimary={disableSubmit()}
      handleClose={handleClose}
    >
      <Grid container padding={2}>
        {
          !resourceType &&
          <ToggleButtonGroup
            exclusive
            value={resourceType || newResourceType}
            onChange={(_, newSelection) => setNewResourceType(newSelection)}
            aria-label="report-schedule-resource-type"
            color="primary"
            disabled={!!resourceType}
            sx={{ mb: 2 }}
          >
            {map(Object.values(CompanyResourceResourceTypeEnum), (type) => (
              <ToggleButton
                size="small"
                key={type}
                value={type}
              >
                {type}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
        <OverwatchResourceSelector
          autoCompleteId={getResourceInfo().id}
          autoCompleteType={getResourceInfo().type as "Employee" | "Equipment" | "Contractor"}
          selection={selectedResource}
          setSelection={setSelectedResource}
          resourceList={optionsFormatter(companyResources?.getCompanyResources || [], (resourceType ? resourceType : newResourceType))}
        />
        <Autocomplete
          options={getShiftOptions(
            projectShifts ? JSON.parse(projectShifts) : ([] as Shift[])
          )}
          renderInput={(params) => (
            <TextField {...params} label="Select Shift" />
          )}
          getOptionLabel={(option) => option.label}
          onChange={(event, newValue) =>
            handleShiftChange(
              event,
              newValue,
              reportDate as string,
              setSelectedShift,
              setScheduleData
            )
          }
          sx={{ mt: 2 }}
          fullWidth
        />
        {selectedShift === "custom" && (
          <Grid container justifyContent="space-between" size={12}>
            <TimeScheduleSection
              startTime={scheduleData.startTime}
              endTime={scheduleData.endTime}
              onChange={handleScheduleTimeChange}
              size="medium"
            />
          </Grid>
        )}
        {showEquipmentOnlySections &&
          <Grid container justifyContent="space-between" mt={2} size={12} spacing={2}>
            <Grid size={6}>
              <Tooltip title="Future Feature: Quantity Selection">
                <TextField
                  label="Quantity"
                  variant="outlined"
                  type="number"
                  disabled
                  fullWidth
                  onChange={(e) => {
                    if (parseInt(e.target.value) < 1) e.target.value = "1";
                    setScheduleData((prev) => ({
                      ...prev,
                      quantity: parseInt(e.target.value),
                    }));
                  }}
                />
              </Tooltip>
            </Grid>
            <Grid size={6}>
              <Tooltip title="Future Feature: Quantity Selection">
                <TextField
                  label="Unit"
                  variant="outlined"
                  type="text"
                  disabled
                  fullWidth
                />
              </Tooltip>
            </Grid>
          </Grid>
        }
        {(showPeopleOnlySections || !showContractorOnlySections) && <Grid container mt={2} size={12}>
          {showPeopleOnlySections &&
            <TextField
              label="Role/Designation (optional)"
              value={scheduleData.title}
              fullWidth
              sx={{ mb: 2 }}
              onChange={(e) =>
                setScheduleData((prev) => ({ ...prev, title: e.target.value }))
              }
            />
          }
          {!showContractorOnlySections &&
            <TextField
              label="Source (optional)"
              fullWidth
              value={scheduleData.source}
              onChange={(e) =>
                setScheduleData((prev) => ({ ...prev, source: e.target.value }))
              }
            />
          }
        </Grid>}
      </Grid>
      <Grid container size={12} px={2}>
        {showContractorOnlySections &&
          <TextField
            label="Description of Service"
            variant="outlined"
            multiline
            rows={5}
            fullWidth
            sx={{ mb: 2 }}
            onChange={(e) =>
              setScheduleData((prev) => ({
                ...prev,
                descriptionOfService: e.target.value,
              }))
            }
          />
        }
        <Autocomplete
          id="address-select"
          key={`project-name-${projectId}`}
          options={addresses || []}
          defaultValue={mainAddress || undefined}
          disableClearable
          fullWidth
          onChange={(_, newValue) =>
            setScheduleData((prev) => ({ ...prev, addressId: newValue.id }))
          }
          getOptionLabel={(option) => formatAddress(option as Address)}
          renderInput={(params) => <TextField {...params} label="Location" />}
          readOnly={arrayLength(subLocations) < 1}
        />
        {showEquipmentOnlySections &&
          <Grid container>
            <FormControlLabel
              control={
                <Switch
                  checked={collectable}
                  onChange={(_, checked) => {
                    if (!onHire) setCollectable(checked);
                  }}
                />
              }
              label="Collectable"
            />
            {isExternalEquipment() && (
              <FormControlLabel
                control={
                  <Switch
                    checked={onHire}
                    onChange={(_, checked) => {
                      setCollectable(checked);
                      setOnHire(checked);
                    }}
                  />
                }
                label="On-Hire"
              />
            )}
          </Grid>
        }
        {showEquipmentOnlySections && collectable && (
          <DeliveryAndCollectionFields
            scheduleData={scheduleData}
            setScheduleData={setScheduleData}
            fieldErrors={collectableFieldErrors}
            setFieldErrors={setCollectableFieldErrors}
          />
        )}
      </Grid>
    </OverwatchModal>
  );
};

export default NewScheduleModal;
